import * as THREE from "three";
import { Cell, Grid } from "@highmarch/domain";
import { Palette, cellToWorld } from "../scene";
import { HQ_YAW, SURFACE_Y, cube, mat, quad } from "./common";
import { GroundMesh } from "../groundMesh";
import { HqMaterials } from "../hqMaterials";
import { Terrain } from "../terrain";
/**
 * The world beyond the map.
 *
 * The world is continuous and unlimited (DESIGN §12): the map never shows a hard edge, and during a battle the haze
 * is what limits how far you can look — leaving room for the rest of the world, and for other players' battles, to
 * sit beyond it. With the ground mesh in play that fade happens inside the ground's own shader, and this view is
 * reduced to a backstop plane in the haze colour. Without the ground mesh (its maps did not ship) a ring of plain
 * ground slabs stands around the map instead.
 */
export class SurroundView {
  readonly group = new THREE.Group();
  /** Plain ground slabs this many cells beyond the grid when the ground mesh is missing; the flat plane takes over past that. */
  static readonly TileRing = 4;
  /** Distance from the map edge where the fog begins and where it is fully opaque, in cells. */
  static readonly FogStart = 1.5;
  static readonly FogFull = 6;
  /**
   * The backstop is a flat plane, so its far edge draws a line across the sky wherever it ends. At 700 units that
   * line sat just under the horizon and read as a seam. Far enough out and the edge falls below the horizon for any
   * camera the game uses.
   */
  static readonly PlaneSize = 6000;

  constructor(grid: Grid) {
    // The slab ring only exists without the ground mesh, which covers this ground itself.
    const tiles = !GroundMesh.available;
    const cx = (grid.width - 1) / 2, cz = (grid.height - 1) / 2;
    const r = tiles ? SurroundView.TileRing : 0;
    for (let y = -r; y < grid.height + r; y++) {
      for (let x = -r; x < grid.width + r; x++) {
        if (x >= 0 && y >= 0 && x < grid.width && y < grid.height) continue;
        this.group.add(cube(cellToWorld(new Cell(x, y), -0.05), 0.96, 0.1, 0.96, Palette.ground));
      }
    }
    // No sea. The world is continuous — the ground mesh runs well past the playable rectangle and dissolves into
    // haze; this plane is only a backstop in the same haze colour, so a low camera angle never sees under the world.
    const plane = new THREE.Mesh(quad, mat(Palette.fog));
    plane.rotation.x = -Math.PI / 2;
    // Just under the ground, so it fills the gap under the apron without ever showing as a horizon of its own. It
    // cannot be a fixed -0.55 now that the land climbs through benches, and it must not be dropped below the gorges
    // either (at -7.5 it silhouetted against the sky as a grey mountain) — the chasms have their own floor mesh, so
    // this only has to clear the lowest ground.
    plane.position.set(cx, SurroundView.underGround(grid), cz);
    plane.scale.set(SurroundView.PlaneSize, SurroundView.PlaneSize, 1);
    this.group.add(plane);
    SurroundView.backstop = plane;
    // No fog sheet over an island. It was a 400-unit cloud plane 1.2 units ABOVE the ground, fully opaque six cells
    // out, and it is what turned everything beyond the island into a featureless pale wash — the sea, the island's
    // own apron and the horizon all disappeared behind it. The distance fog and the sea now do that job in depth.
    // No fog sheet: the ground shader does the map-edge fade itself now (DESIGN §12), in depth, without a 400-unit
    // opaque plane hanging over the world.
  }

  /** The lowest the rolling ground gets over the map and its apron, less a little clearance. */
  static underGround(grid: Grid): number {
    const T = Terrain.current;
    if (!T) return -0.55;
    let lo = 0;
    for (let z = -24; z <= grid.height + 24; z += 3) for (let x = -24; x <= grid.width + 24; x += 3) lo = Math.min(lo, T.hillAt(x, z));
    return lo - 0.6;
  }

  /** The backstop plane, so the look can keep it in the haze colour. */
  static backstop: THREE.Mesh | null = null;

  /** Retints the backstop for a look preset (called by the scene when the look changes). */
  static applyLook(haze: number): void {
    const m = SurroundView.backstop?.material as THREE.MeshBasicMaterial | undefined;
    if (m?.color) m.color.set(haze);
  }

  /** A translucent sheet above the ground: clear over the map, solid cloud from `FogFull` cells beyond its edge. */
  private fogSheet(grid: Grid, cx: number, cz: number): THREE.Mesh {
    const size = 1024;
    const span = SurroundView.PlaneSize;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    const img = ctx.createImageData(size, size);
    const fog = new THREE.Color(Palette.fog).convertLinearToSRGB();
    const halfW = grid.width / 2, halfH = grid.height / 2;
    const [fr, fg, fb] = [fog.r * 255, fog.g * 255, fog.b * 255];
    for (let j = 0; j < size; j++) {
      const wz = ((j + 0.5) / size - 0.5) * span;
      const dz = Math.max(0, Math.abs(wz) - halfH);
      for (let i = 0; i < size; i++) {
        const wx = ((i + 0.5) / size - 0.5) * span;
        const dx = Math.max(0, Math.abs(wx) - halfW);
        const t = THREE.MathUtils.smoothstep(Math.hypot(dx, dz), SurroundView.FogStart, SurroundView.FogFull);
        const k = (j * size + i) * 4;
        img.data[k] = fr; img.data[k + 1] = fg; img.data[k + 2] = fb; img.data[k + 3] = t * 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    const sheet = new THREE.Mesh(quad, new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, fog: false }));
    sheet.rotation.x = -Math.PI / 2;
    sheet.position.set(cx, SURFACE_Y + 1.2, cz);
    sheet.scale.set(span, span, 1);
    sheet.renderOrder = 10;
    return sheet;
  }
}
